import {
  ColorSwatches,
  DonationAmountSelection,
  DonationFormType,
  DonationPopupModalType,
  FundRaiserRedirectType,
  FundRaiserStatus,
  FundRaiserTemplateLayoutType,
  IconType,
  LegalTermsAndConditionType,
  MediaType,
  RedirectOtherFundraiserType,
  SystemItemTypes,
} from "../enums";
import {
  getNewErrors,
  IColor,
  InputErrorModel,
  UtilityMethods,
} from "@mindmemobile/mm-shared-global";
import {
  IAPIFundraiserGridModel,
  IFundImpactAPIResponse,
  IFundRaiserCallToAction,
  IFundRaiserMarketing,
  IFundraiserMissionIAPIResponse,
  IFundRaiserRedirectValue,
  ICreateFundraiserIAPIResponse,
  IFundraiserData,
  IPromoter,
  IDonationButtonColors,
  CustomColorConfig,
  ISectionVisibility,
  IFundStatsIAPIResponse,
  IAPIGetFundraiserPublicViewResponseModel,
  IFundraisersDonationsBoardResponse,
  IFundRaiserPageDesign,
  IFundRaiserCustomColors,
  IAPIItemsGridModel,
  ICategoryModel,
  IFundraiserPublicViewPromoterRewardResponseModel,
  IFundraiserPublicViewPromoterResponseModel,
  IFundraisersDailyDonations,
  IAPIPromoterProgressModel,
  IAPIRelatedFundraisers,
  IDonationButtonDesign,
  IUpdateOpenAISettingsRequestModel,
  ISaveOpenAISettingsRequestModel,
} from "../interfaces";
import {
  Fundraiser,
  ImpactModel,
  RankAchievementModel,
  RewardModel,
} from "./FundraisersGridModels";
import { ColorModel } from "./GalleryModels";
import { MediaModel } from "./GalleryModels";
import { DonationItemModel, DonorInfo } from "./DonationModels";
import { ListInterestTagModel } from "./ListInterestTagModels";
import { PromoterProgressModel } from "./PromoterModels";
import {
  CheckOutPayFields,
  ContactPhoneModel,
  ContactReferenceModel,
  CountryWithStateModel,
  CustomerFacingCheckOutModel,
  IAPICheckOutPaymentModel,
  IAPICheckOutPurchaseModel,
  MerchantAccount,
  PaymentMethodModel,
  PurchasePaymentIntentModel,
  RGBA,
} from "@mindmemobile/mm-payment-modules";
import { fundraiserStore } from "../stores";
import { autorun } from "mobx";
import { formatCurrencyAmount } from "../components/fundraiser-builder/donation/DonationFormSection";
import { colorTemplates } from "../components";

export type BuilderSectionId =
  | "details"
  | "media"
  | "mission"
  | "impact"
  | "stats"
  | "callToAction"
  | "marketing"
  | "legal"
  | "donation"
  | "design"
  | "promoters";

export class CreateFundraiserModel implements IFundraiserData {
  accountId: string;
  pageName: string;
  isCreateNewDonationItem: boolean;
  newDonationItemName: string;
  id: string;
  rank: number;
  status: keyof typeof FundRaiserStatus;
  name: string;
  title: string;
  subtitle: string;
  description: string;
  goal: number;
  startDate: Date;
  endDate: Date | null;
  category: CategoryModel;
  iconImageChoice: keyof typeof IconType;
  titleIcon: string;
  titleColor: ColorModel;
  fundraiserImage: MediaModel;
  mediaItems: MediaModel[];
  rotateMedia: boolean;
  missionTitle: string;
  missionDescription: string;
  impact: ImpactModel[];
  fundraiserStats: FundRaiserStatsModel[];
  ctaTitle: string;
  ctaDescription: string;
  ctaButtonText: string;
  ctaIcon: string;
  ctaIconImageChoice: keyof typeof IconType;
  ctaImage: MediaModel;
  ctaIconColor: ColorModel;
  showCtaButton: boolean;
  donationItem: DonationItemModel | null;
  isCustomDonationItem: boolean;
  donationAmountsType: DonationAmountSelection;
  customDonationAmounts: number[];
  allowCustomDonations: boolean;
  allowMonthlyDonations: boolean;
  merchantAccount: MerchantAccount;
  donationFormType: DonationFormType;
  popupModalType: DonationPopupModalType;
  donationRestrictionsEnabled: boolean;
  minDonationAmount: string;
  maxDonationAmount: string;
  // Surcharge: charge donors a % fee to cover processing costs.
  surchargeEnabled: boolean;
  surchargePercentage: string;
  surchargeAskCustomerFirst: boolean;
  selectedLists: ListInterestTagModel[];
  selectedInterests: ListInterestTagModel[];
  selectedTags: ListInterestTagModel[];
  redirectOnVisit: keyof typeof FundRaiserRedirectType;
  redirectWebsiteUrl: string;
  redirectMobilePage: string;
  redirectOtherFundraiserType: keyof typeof RedirectOtherFundraiserType;
  redirectSpecificFundraiserIds: string[];
  promoterGoal: number;
  closeOnGoalAchieved: boolean;
  increaseGoalOnAchieved: boolean;
  increaseGoalPercentage: string;
  allowPromoterInvites: boolean;
  includeRecruitedActivity: boolean;
  rewards: RewardModel[];
  ranks: RankAchievementModel[];
  bannerImage: MediaModel;
  legalName: string;
  streetAddress1: string;
  streetAddress2: string;
  city: string;
  state: string;
  stateId: string;
  zip: string;
  country: CountryWithStateModel;
  countryId: string;
  termsType: keyof typeof LegalTermsAndConditionType;
  termsUrl: string;
  termsUrlError: boolean;
  termsText: string;
  termsTextError: boolean;
  termsRequired: boolean;
  selectedLayout: keyof typeof FundRaiserTemplateLayoutType;
  sectionVisibility: FundraiserSectionVisibilityModel;
  layoutZones: Record<string, string[]>;
  progressLayout: string;
  customColors: FundraiserCustomColorConfig;
  selectedColorSwatch: keyof typeof ColorSwatches;
  donationButtonType: string;
  donationButtonColors: FundraiserDonationButtonColors;
  promoterCount: number;
  recentDonationsCount: number;
  showDonationComments: boolean;
  promoterGridData: PromoterProgressModel[];
  showMainSectionLibrary: boolean;
  showMediaSectionLibrary: boolean;
  showCallToActionSectionLibrary: boolean;
  showMarketingSectionLibrary: boolean;

  //public view properties
  donationsBoard?: FundraisersDonationsBoardResponse[];
  leaderboard?: PromoterProgressModel[];
  rewardsByRank?: FundraiserPublicViewRewardsByRank[];
  totalRaisedAmount: number;
  totalDonations: number;
  totalDonors: number;
  activePromoters: number;
  createdDate: Date;
  lastDonatedOn?: Date;
  donationCustomerFacingCheckOutDetails: CustomerFacingCheckOutModel | null;
  dailyDonations?: FundraiserDailyDonationsModel[];
  errors: InputErrorModel<CreateFundraiserModel>[];
  //related to promoter view
  promoterProgress: PromoterProgressModel;
  relatedFundraisers: RelatedFundRaisersModel[];
  galleryId?: string;
  //Use in edit mode only
  shortUniqueId: string;
  galleryPageName?: string;
  constructor(data?: Partial<CreateFundraiserModel>) {
    this.pageName = data?.pageName ?? "";
    this.accountId = data?.accountId ?? "";
    this.isCreateNewDonationItem = data?.isCreateNewDonationItem ?? false;
    this.newDonationItemName = data?.newDonationItemName ?? "";
    this.id = data?.id ?? "";
    this.name = data?.name ?? "";
    this.title = data?.title ?? "";
    this.subtitle = data?.subtitle ?? "";
    this.description = data?.description ?? "";
    this.goal = data?.goal ?? 500;
    this.startDate = data?.startDate ?? new Date();
    this.endDate = data?.endDate ?? null;
    this.category = data?.category
      ? new CategoryModel(data.category)
      : new CategoryModel();
    this.iconImageChoice = data?.iconImageChoice ?? IconType.None;
    this.titleIcon = data?.titleIcon || "Heart";
    this.errors =
      (data &&
        data.errors?.map((e) => {
          return new InputErrorModel<CreateFundraiserModel>(e);
        })) ||
      [];
    this.titleColor = data?.titleColor
      ? new ColorModel(data?.titleColor)
      : new ColorModel({
          rgba: "rgba(16, 185, 129, 1)",
          rgbValue: { r: "16", g: "185", b: "129", a: "1" },
          hex: "#10b981",
        });
    this.mediaItems = data?.mediaItems ?? [];
    this.fundraiserImage = data?.fundraiserImage
      ? new MediaModel(data.fundraiserImage)
      : new MediaModel();
    this.showMainSectionLibrary = data?.showMainSectionLibrary ?? false;
    this.showMediaSectionLibrary = data?.showMediaSectionLibrary ?? false;
    this.showCallToActionSectionLibrary =
      data?.showCallToActionSectionLibrary ?? false;
    this.showMarketingSectionLibrary =
      data?.showMarketingSectionLibrary ?? false;
    this.mediaItems = data?.mediaItems
      ? data.mediaItems.map((item) => new MediaModel(item))
      : [];
    this.rotateMedia = data?.rotateMedia ?? false;
    this.missionTitle = data?.missionTitle || "";
    this.missionDescription = data?.missionDescription ?? "";
    this.impact = data?.impact ?? [];
    this.fundraiserStats =
      data?.fundraiserStats?.map((s) => new FundRaiserStatsModel(s)) || [];
    this.ctaTitle = data?.ctaTitle ?? "";
    this.ctaDescription = data?.ctaDescription ?? "";
    this.ctaButtonText = data?.ctaButtonText ?? "Learn More";
    this.ctaIcon = data?.ctaIcon ?? "";
    this.ctaIconImageChoice = data?.ctaIconImageChoice || IconType.None;
    this.ctaImage = data?.ctaImage
      ? new MediaModel(data.ctaImage)
      : new MediaModel();
    this.ctaIconColor = data?.ctaIconColor
      ? new ColorModel(data.ctaIconColor)
      : new ColorModel();
    this.showCtaButton = data?.showCtaButton ?? false;
    this.donationItem = data?.donationItem ?? null;
    this.isCustomDonationItem = data?.isCustomDonationItem ?? false;
    this.donationAmountsType =
      data?.donationAmountsType ?? DonationAmountSelection.Default;
    this.customDonationAmounts = data?.customDonationAmounts ?? [
      25, 50, 100, 150, 200, 250,
    ];
    this.allowCustomDonations = data?.allowCustomDonations ?? false;
    this.allowMonthlyDonations = data?.allowMonthlyDonations ?? false;
    this.merchantAccount = data?.merchantAccount
      ? new MerchantAccount(data.merchantAccount)
      : new MerchantAccount();
    this.donationFormType = data?.donationFormType ?? DonationFormType.Embedded;
    this.popupModalType = data?.popupModalType ?? DonationPopupModalType.Simple;
    this.donationRestrictionsEnabled =
      data?.donationRestrictionsEnabled ?? false;
    this.minDonationAmount = data?.minDonationAmount ?? "";
    this.maxDonationAmount = data?.maxDonationAmount ?? "";
    this.surchargeEnabled = data?.surchargeEnabled ?? false;
    this.surchargePercentage = data?.surchargePercentage ?? "";
    this.surchargeAskCustomerFirst = data?.surchargeAskCustomerFirst ?? false;
    this.selectedLists = data?.selectedLists ?? [];
    this.selectedInterests = data?.selectedInterests ?? [];
    this.selectedTags = data?.selectedTags ?? [];
    this.redirectOnVisit = data?.redirectOnVisit ?? FundRaiserRedirectType.None;
    this.redirectWebsiteUrl = data?.redirectWebsiteUrl ?? "";
    this.redirectMobilePage = data?.redirectMobilePage ?? "";
    this.redirectOtherFundraiserType =
      data?.redirectOtherFundraiserType ??
      RedirectOtherFundraiserType.ClosestGoal;
    this.redirectSpecificFundraiserIds =
      data?.redirectSpecificFundraiserIds ?? [];
    this.promoterGoal = data?.promoterGoal ?? 0;
    this.closeOnGoalAchieved = data?.closeOnGoalAchieved ?? false;
    this.increaseGoalOnAchieved = data?.increaseGoalOnAchieved ?? false;
    this.increaseGoalPercentage = data?.increaseGoalPercentage ?? "10";
    this.allowPromoterInvites = data?.allowPromoterInvites ?? false;
    this.includeRecruitedActivity = data?.includeRecruitedActivity ?? false;
    this.rewards = data?.rewards ?? [];
    this.ranks = data?.ranks ?? [];
    this.bannerImage = data?.bannerImage
      ? new MediaModel(data.bannerImage)
      : new MediaModel();
    this.legalName = data?.legalName ?? "";
    this.streetAddress1 = data?.streetAddress1 ?? "";
    this.streetAddress2 = data?.streetAddress2 ?? "";
    this.city = data?.city ?? "";
    this.state = data?.state ?? "";
    this.stateId = data?.stateId ?? "";
    this.zip = data?.zip ?? "";
    this.country = data?.country
      ? new CountryWithStateModel(data.country)
      : new CountryWithStateModel();
    this.countryId = data?.countryId ?? "";
    this.termsType = data?.termsType ?? LegalTermsAndConditionType.None;
    this.termsUrl = data?.termsUrl ?? "";
    this.termsUrlError = data?.termsUrlError ?? false;
    this.termsText = data?.termsText ?? "";
    this.termsTextError = data?.termsTextError ?? false;
    this.termsRequired = data?.termsRequired ?? false;
    this.selectedLayout =
      data?.selectedLayout ?? FundRaiserTemplateLayoutType.Layout1;
    this.sectionVisibility =
      data?.sectionVisibility ?? new FundraiserSectionVisibilityModel();
    this.layoutZones = data?.layoutZones ?? {
      topZone: [
        "mission",
        "impact",
        "fundraiserProgress",
        "promoterProgress",
        "stats",
      ],
      leftZone: ["media", "donation"],
      rightZone: ["cta", "promoters", "recent"],
      bottomZone: ["description", "sponsor"],
    };
    this.progressLayout = data?.progressLayout ?? "stacked";
    this.customColors = data?.customColors
      ? new FundraiserCustomColorConfig(data.customColors)
      : new FundraiserCustomColorConfig();
    this.selectedColorSwatch =
      data?.selectedColorSwatch ?? ColorSwatches.Default;
    this.donationButtonType = data?.donationButtonType ?? "solid";
    this.donationButtonColors =
      data?.donationButtonColors ?? new FundraiserDonationButtonColors();
    this.promoterCount = data?.promoterCount ?? 0;
    this.recentDonationsCount = data?.recentDonationsCount ?? 0;
    this.showDonationComments = data?.showDonationComments ?? false;
    this.promoterGridData = data?.promoterGridData ?? [];
    this.status = data?.status ?? FundRaiserStatus.Draft;
    this.leaderboard = data?.leaderboard?.map(
      (l) => new PromoterProgressModel(l)
    );
    this.donationsBoard = data?.donationsBoard?.map(
      (d) => new FundraisersDonationsBoardResponse(d)
    );
    this.rewardsByRank = data?.rewardsByRank?.map(
      (r) => new FundraiserPublicViewRewardsByRank(r)
    );

    this.totalRaisedAmount = data?.totalRaisedAmount ?? 0;
    this.totalDonations = data?.totalDonations ?? 0;
    this.totalDonors = data?.totalDonors ?? 0;
    this.activePromoters = data?.activePromoters ?? 0;
    this.rank = data?.rank ?? 0;
    this.createdDate = data?.createdDate
      ? new Date(data.createdDate)
      : new Date();
    this.lastDonatedOn = data?.lastDonatedOn
      ? new Date(data.lastDonatedOn)
      : undefined;
    this.donationCustomerFacingCheckOutDetails =
      data?.donationCustomerFacingCheckOutDetails
        ? new CustomerFacingCheckOutModel(
            data.donationCustomerFacingCheckOutDetails
          )
        : null;
    this.dailyDonations = data?.dailyDonations
      ? data.dailyDonations.map((d) => new FundraiserDailyDonationsModel(d))
      : [];
    this.promoterProgress = data?.promoterProgress
      ? new PromoterProgressModel(data.promoterProgress)
      : new PromoterProgressModel();
    this.relatedFundraisers = data?.relatedFundraisers
      ? data.relatedFundraisers.map((r) => new RelatedFundRaisersModel(r))
      : [];
    this.galleryId = data?.galleryId || "";
    this.shortUniqueId = data?.shortUniqueId || "";
    this.galleryPageName = data?.galleryPageName || "";
    autorun(() => {
      this.validate(true);
    });
  }

  getCustomColors() {
    const selectedTemplate =
      colorTemplates.find((t) => t.id === this.selectedColorSwatch) ||
      colorTemplates[0];
    const customColors =
      this.selectedColorSwatch === "Custom"
        ? this.customColors
        : FundraiserCustomColorConfig.deserializeAPIModel(
            selectedTemplate.colors
          );
    return customColors;
  }

  addHttp(url) {
    if (!/^https?:\/\//i.test(url)) {
      return "https://" + url;
    }
    return url;
  }

  getTermsUrlWithHttp(): string {
    return this.addHttp(this.termsUrl);
  }

  getPrimaryImageToShow(): string {
    if (this.mediaItems && this.mediaItems.length > 0) {
      return (
        this.mediaItems.filter(
          (m) => m.type?.toLocaleLowerCase() === "image"
        )[0]?.fullURL || ""
      );
    }
    return "";
  }

  deserializeColor(color: ColorModel): IColor {
    return color as IColor;
  }

  remainingAmount() {
    return Math.max(0, this.goal - this.totalRaisedAmount);
  }

  getErrorMessage = (field: keyof CreateFundraiserModel): string => {
    const error = this.errors.find((error) => error.field === field);
    return error ? error.message || "" : "";
  };

  hasError = (field: keyof CreateFundraiserModel): boolean => {
    return this.errors.some((error) => error.field === field);
  };

  validate(
    isAutoRun = false,
    fundraisers: CreateFundraiserModel[] = [],
    setCurrentStep?: (step: number) => void,
    navigateToSection?: (
      section: BuilderSectionId,
      shouldScroll: boolean
    ) => void
  ): boolean {
    const newErrors: InputErrorModel<CreateFundraiserModel>[] = [];
    let firstErrorSection: BuilderSectionId | undefined;
    let shouldScrollToSection = false;

    const setError = (
      field: keyof CreateFundraiserModel,
      message: string,
      section: BuilderSectionId
    ) => {
      newErrors.push(new InputErrorModel({ field, message }));
      if (!firstErrorSection) firstErrorSection = section;
    };

    if (!this.name?.trim())
      setError("name", "Fundraiser name is required", "details");
    if (!this.title?.trim())
      setError("title", "Fundraiser title is required", "details");
    if (!this.startDate)
      setError("startDate", "Start date is required", "details");

    if (
      this.startDate &&
      this.endDate &&
      new Date(this.endDate) < new Date(this.startDate)
    ) {
      setError("endDate", "End date cannot be before start date", "details");
    }

    if (this.name?.trim()) {
      if (fundraisers) {
        const duplicate = fundraisers.some(
          (f) =>
            f.id !== this.id &&
            f.name.toLowerCase() === this.name.trim().toLowerCase()
        );
        if (duplicate)
          setError("name", "Fundraiser name must be unique", "details");
      }
    }

    if (this.impact.length > 0) {
      this.impact.forEach((impact, index) => {
        if (!impact.description?.trim() || !impact.number) {
          setError(
            `impact${index}` as keyof CreateFundraiserModel,
            "Impact description and number are required",
            "impact"
          );
        }
      });
    }

    if (!this.mediaItems?.length) {
      setError("mediaItems", "At least one media item is required", "media");
      shouldScrollToSection = true;
    }

    if (this.fundraiserStats.length > 0) {
      this.fundraiserStats.forEach((stat, index) => {
        if (!stat.label?.trim() || !stat.value?.trim()) {
          setError(
            `fundraiserStats${index}` as keyof CreateFundraiserModel,
            "Stat label and value are required",
            "stats"
          );
        }
      });
      shouldScrollToSection = true;
    }

    if (this.donationRestrictionsEnabled) {
      if (!this.minDonationAmount?.trim() && !this.maxDonationAmount?.trim()) {
        setError("minDonationAmount", "Minimum amount required", "donation");
        setError("maxDonationAmount", "Maximum amount required", "donation");
      } else if (!this.minDonationAmount?.trim()) {
        setError("minDonationAmount", "Minimum amount required", "donation");
      } else if (!this.maxDonationAmount?.trim()) {
        setError("maxDonationAmount", "Maximum amount required", "donation");
      }

      const min = parseFloat(this.minDonationAmount?.replace(/,/g, "") || "0");
      const max = parseFloat(this.maxDonationAmount?.replace(/,/g, "") || "0");

      if (min && (isNaN(min) || min <= 0)) {
        setError(
          "minDonationAmount",
          "Minimum amount must be greater than $0",
          "donation"
        );
      }

      if (max && (isNaN(max) || max <= 0))
        setError(
          "maxDonationAmount",
          "Maximum amount must be greater than $0",
          "donation"
        );

      if (min >= max && min > 0 && max > 0) {
        setError(
          "minDonationAmount",
          "Minimum must be less than maximum",
          "donation"
        );
        setError(
          "maxDonationAmount",
          "Maximum must be greater than minimum",
          "donation"
        );
      }
    }

    if (this.surchargeEnabled) {
      const percentage = parseFloat(
        this.surchargePercentage?.replace(/,/g, "") || "0"
      );
      if (isNaN(percentage) || percentage < 0.1 || percentage > 4) {
        setError(
          "surchargePercentage",
          "Value must be between 0.1% and 4%.",
          "donation"
        );
      }
    }

    if (!this.isCreateNewDonationItem && !this.donationItem?.id) {
      setError("donationItem", "Donation item is required", "donation");
    }

    if (!this.merchantAccount.merchantAccountId || !this.merchantAccount.id) {
      setError("merchantAccount", "Merchant account is required", "donation");
    }

    if (
      this.termsType === LegalTermsAndConditionType.CustomTerms &&
      !this.termsText?.trim()
    ) {
      setError("termsText", "Custom terms text is required", "legal");
      setError("termsTextError", "Custom terms text is required", "legal");
    }

    this.errors = isAutoRun ? getNewErrors(newErrors, this.errors) : newErrors;

    if (!isAutoRun && newErrors.length > 0 && firstErrorSection) {
      this.handleSectionNavigation(
        firstErrorSection,
        shouldScrollToSection,
        setCurrentStep,
        navigateToSection
      );
    }

    return this.errors.length === 0;
  }

  private handleSectionNavigation(
    section: BuilderSectionId,
    shouldScroll: boolean,
    setCurrentStep?: (step: number) => void,
    navigateToSection?: (
      section: BuilderSectionId,
      shouldScroll: boolean
    ) => void
  ) {
    const stepMap: Record<BuilderSectionId, number> = {
      details: 1,
      media: 1,
      mission: 1,
      impact: 1,
      stats: 1,
      callToAction: 1,
      marketing: 1,
      legal: 1,
      donation: 2,
      design: 3,
      promoters: 4,
    };

    const step = stepMap[section];
    setCurrentStep?.(step);
    navigateToSection?.(section, shouldScroll);
  }

  static apiRequestDateFormat(date: Date) {
    return new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
    ).toISOString();
  }

  static createAPIRequest(
    userId: string,
    accountId: string,
    data: CreateFundraiserModel,
    layoutZones: Record<string, string[]>
  ): ICreateFundraiserIAPIResponse {
    return {
      userId,
      accountId,
      icon: data.titleIcon,
      name: data.name,
      title: data.title,
      subTitle: data.subtitle ?? "",
      description: data.description,
      category: data.category?.name ? data.category.createAPIModel() : null,
      rotateMedia: data.rotateMedia,
      startDate: data.startDate
        ? this.apiRequestDateFormat(data.startDate)
        : new Date().toISOString(),
      endDate: data.endDate ? this.apiRequestDateFormat(data.endDate) : null,
      iconType: data.iconImageChoice || IconType.None,
      color: new ColorModel({
        hex: data.titleColor?.hex,
      }).createAPIModel(),
      image:
        data.iconImageChoice === IconType.Image && data.fundraiserImage
          ? data.fundraiserImage.createAPIModel()
          : null,
      medias: data.mediaItems?.map((media) => media.createAPIModel()) || [],
      // map from mediaItems if you add them back

      mission: {
        title: data.missionTitle,
        description: data.missionDescription,
      } as IFundraiserMissionIAPIResponse,
      impacts: (data.impact ?? []).map(
        (i): IFundImpactAPIResponse => ({
          value: i.number,
          description: i.description,
          icon: i.icon,
          color: new ColorModel(i.color).createAPIModel(),
        })
      ),
      stats:
        data.fundraiserStats?.map((stat) => ({
          name: stat.label,
          value: stat.value,
        })) || [],
      clickToAction:
        data.ctaTitle || data.ctaImage.mediaId
          ? ({
              iconType: data.ctaIconImageChoice || IconType.None,
              icon: data.ctaIcon,
              title: data.ctaTitle,
              description: data.ctaDescription,
              buttonText: data.ctaButtonText,
              image:
                data.ctaIconImageChoice === IconType.Image && data.ctaImage
                  ? data.ctaImage.createAPIModel()
                  : null,
              color: data.ctaIconColor.createAPIModel(),
            } as IFundRaiserCallToAction)
          : null,
      marketing: {
        goalAmount: data.goal ?? 0,
        promoterGoalAmount: data.promoterGoal ?? 0,
        closeOnGoalAchieved: data.closeOnGoalAchieved ?? false,
        isIncreaseGoal: data.increaseGoalOnAchieved ?? false,
        increaseGoalPercentage: data.increaseGoalPercentage || 0,
        allowPromotersToRecruit: data.allowPromoterInvites ?? false,
        includeRecruitedPromoterActivityInPromoterGoals:
          data.includeRecruitedActivity === true,
        rewardsByAmount:
          data.rewards
            ?.filter((r) => (r.level ? true : false))
            ?.map((r) => ({
              amount: r.level,
              reward: r.reward,
            })) || [],
        rewardByRank:
          data.ranks
            ?.filter((r) => r.reward)
            ?.map((r) => {
              const [start, end] = r.rank
                .split("-")
                .map((n) => parseInt(n.trim(), 10));
              return {
                startingRank: start || parseInt(r.rank),
                endingRank: end || start || parseInt(r.rank),
                reward: r.reward,
                minimumAmount: r.minimum,
              };
            }) || [],
        sponserBanners: data.bannerImage
          ? [data.bannerImage.createAPIModel()]
          : null,
        legalDetails: {
          name: data.legalName,
          address1: data.streetAddress1,
          address2: data.streetAddress2,
          city: data.city,
          country: data.country.abbreviationName,
          countryId: data.country.id ? data.country.id : null,
          state: data.state,
          zipCode: data.zip,
          stateId: data.stateId ? data.stateId : null,
          customTerms:
            data.termsType === LegalTermsAndConditionType.CustomTerms
              ? data.termsText
              : "",
          referenceURL:
            data.termsType === LegalTermsAndConditionType.ReferenceURL
              ? data.termsUrl
              : "",
          termsAndConditionType: data.termsType,
          requireDonorAcceptence:
            data.termsType !== LegalTermsAndConditionType.None &&
            data.termsRequired,
        },
      } as IFundRaiserMarketing,
      donationAndPaymentDetails: {
        isCreateDonation: data.isCreateNewDonationItem,
        donationName: data.newDonationItemName,
        donationItemDetails: data.donationItem?.id ? data.donationItem : null,
        showPopUp: data.donationFormType === DonationFormType.Popup,
        donationItem:
          data.popupModalType === DonationPopupModalType.DonationItem,
        restrictions: data.donationRestrictionsEnabled,
        minimumAmount: data.minDonationAmount
          ? parseFloat(data.minDonationAmount?.replace(/,/g, "") || "0")
          : 0,
        maximumAmount: data.maxDonationAmount
          ? parseFloat(data.maxDonationAmount?.replace(/,/g, "") || "0")
          : 0,
        surchargeEnabled: data.surchargeEnabled,
        surchargePercentage: data.surchargePercentage
          ? parseFloat(data.surchargePercentage?.replace(/,/g, "") || "0")
          : 0,
        surchargeAskCustomerFirst: data.surchargeAskCustomerFirst,
        redirect: data.redirectOnVisit !== FundRaiserRedirectType.None,
        redirectType: data.redirectOtherFundraiserType,
        redirectValue: CreateFundraiserModel.getRedirectValue(data),
        amountSelection: data.donationAmountsType,
        allowCustomDonations: data.allowCustomDonations === true,
        allowMonthlyDonations: data.allowMonthlyDonations === true,
        customAmounts: data.customDonationAmounts,
        merchantAccount: data.merchantAccount?.id
          ? data.merchantAccount.createAPIModel2()
          : null,
        lists:
          data.selectedLists?.map((list) => {
            return {
              id: list.id,
              name: list.name,
            };
          }) || [],
        interests:
          data.selectedInterests?.map((interest) => {
            return {
              id: interest.id,
              name: interest.name,
            };
          }) || [],
        tags:
          data.selectedTags?.map((tag) => {
            return {
              id: tag.id,
              name: tag.name,
            };
          }) || [],
      },
      design: {
        layout: data.selectedLayout,
        showCallToAction: data.sectionVisibility?.cta === true,
        showCampaignProgress: true, // this.sectionVisibility?.campaignProgress === true,
        showDonationComments: data.showDonationComments,
        showHeader: true, // this.sectionVisibility?.header === true,
        showDonationForm: data.sectionVisibility?.donation === true,
        showHeroMedia: data.sectionVisibility?.media === true,
        showImpactStats: data.sectionVisibility?.impact === true,
        showLeaderBoard: data.sectionVisibility?.promoters === true,
        showMissionSection: data.sectionVisibility?.mission === true,
        showPromotersProgress: true, //data.sectionVisibility?.promoterProgress === true,
        showRecentDonations: true, //this.sectionVisibility?.recentDonations === true,
        customColors: {
          primary: new ColorModel(data.customColors?.primary),
          secondary: new ColorModel(
            data.customColors?.secondary
          ).createAPIModel(),
          accent: new ColorModel(data.customColors?.accent).createAPIModel(),
          text: new ColorModel(data.customColors?.text).createAPIModel(),
          background: new ColorModel(
            data.customColors?.background
          ).createAPIModel(),
        },
        buttonDesign: {
          solidButton: data.donationButtonType === "solid",
          text: data.donationButtonColors?.text?.createAPIModel() || null,
          color:
            data.donationButtonType === "solid"
              ? data.donationButtonColors?.primary?.createAPIModel()
              : null,
          startColor:
            data.donationButtonType !== "solid"
              ? data.donationButtonColors?.primary?.createAPIModel()
              : null,
          endColor:
            data.donationButtonType !== "solid"
              ? data.donationButtonColors?.secondary?.createAPIModel()
              : null,
        },
        totalLeadBoards: data.promoterCount || 0,
        totalLastDonations: data.recentDonationsCount || 0,
        colorSwatch: data.selectedColorSwatch,
        showStats: data.sectionVisibility?.stats === true,
        showProgress: data.sectionVisibility?.progress === true,
        fundRaiserProgress: data.sectionVisibility?.fundraiserProgress === true,
        promoterProgress: data.sectionVisibility?.promoterProgress === true,
        sideBySideProgress: data.progressLayout === "side-by-side",
        topZones: layoutZones?.topZone || [],
        leftZones: layoutZones?.leftZone || [],
        rightZones: layoutZones?.rightZone || [],
        bottomZones: layoutZones?.bottomZone || [],
      },
      promoters:
        data.promoterGridData
          ?.filter((p) => p.contactId || p.mindmeContactId)
          ?.map((p) => ({
            contactId: p.contactId || null,
            mindMeContactId: p.mindmeContactId || null,
            isActive: p.isActive === true,
            goalAmount: p.goalAmount || 0,
          })) || [],
    };
  }

  static getRedirectValue(
    data: CreateFundraiserModel
  ): IFundRaiserRedirectValue | null {
    switch (data.redirectOtherFundraiserType) {
      case RedirectOtherFundraiserType.Specific:
        return data.redirectSpecificFundraiserIds?.length > 0
          ? {
              ids: data.redirectSpecificFundraiserIds,
            }
          : null;
      case RedirectOtherFundraiserType.Website:
        return {
          url: data.redirectWebsiteUrl,
        };
      case RedirectOtherFundraiserType.MobilePage:
        return {
          id: data.redirectMobilePage,
        };
      default:
        return null;
    }
  }

  static deserializeAPIModel(
    data: IAPIGetFundraiserPublicViewResponseModel,
    isPublicView?: boolean
  ): CreateFundraiserModel {
    if (
      !data?.isFundraiserAvailable &&
      data?.fundraiserRedirection?.redirectValue &&
      isPublicView
    ) {
      switch (data.fundraiserRedirection.redirectType) {
        case RedirectOtherFundraiserType.AnyActive:
        case RedirectOtherFundraiserType.ClosestGoal:
        case RedirectOtherFundraiserType.Specific:
        case RedirectOtherFundraiserType.Gallery:
          window.location.href =
            window.location.origin +
            `/${data.fundraiserRedirection.redirectValue}`;
          break;
        default:
          let url = data.fundraiserRedirection.redirectValue;
          if (!/^https?:\/\//i.test(url)) {
            url = "https://" + url;
          }
          window.location.href = url;
          break;
      }
    }
    const layoutZones: Record<string, string[]> = {
      topZone: data.design?.topZones || [],
      leftZone: data.design?.leftZones || [],
      rightZone: data.design?.rightZones || [],
      bottomZone: data.design?.bottomZones || [],
    };
    return new CreateFundraiserModel({
      shortUniqueId: data.shortUniqueId ?? "",
      galleryId: data.galleryId ?? "",
      galleryPageName: data.galleryPageName ?? "",
      id: data.id ?? "",
      accountId: data.accountId ?? "",
      status: data.status as keyof typeof FundRaiserStatus,
      name: data.name ?? "",
      title: data.title ?? "",
      subtitle: data.subTitle ?? "",
      description: data.description ?? "",
      goal: data.marketing?.goalAmount ?? 0,
      totalRaisedAmount: data.totalRaisedAmount ?? 0,
      totalDonations: data.totalDonations ?? 0,
      totalDonors: data.totalDonors ?? 0,
      rank: data.rank ?? 0,
      activePromoters: data.activePromoters ?? 0,
      createdDate: data.createdDate ? new Date(data.createdDate) : new Date(),
      startDate: data.startDate ? new Date(data.startDate) : new Date(),
      endDate: data.endDate ? new Date(data.endDate) : null,
      newDonationItemName: data.donationAndPaymentDetails.isCreateDonation
        ? data.donationAndPaymentDetails.donationName
        : "",
      category: data.category
        ? CategoryModel.deserializeAPIModel(data.category)
        : new CategoryModel(),
      iconImageChoice: data.iconType ?? IconType.None,
      titleIcon: data.icon ?? "",
      titleColor: ColorModel.deserializeAPIModel(data.color),
      fundraiserImage: data.image
        ? MediaModel.deserializeAPIModel(data.image)
        : new MediaModel(),
      mediaItems:
        data.medias?.map((m) => MediaModel.deserializeAPIModel(m)) ?? [],
      rotateMedia: data.rotateMedia ?? false,
      missionTitle: data.mission?.title ?? "",
      missionDescription: data.mission?.description ?? "",
      impact:
        data.impacts?.map((i, index) =>
          ImpactModel.deserializeAPIModel(i, index)
        ) ?? [],
      fundraiserStats:
        data.stats?.map((s) =>
          FundRaiserStatsModel.deserializeAPIModel(s, 0)
        ) ?? [],
      ctaTitle: data.clickToAction?.title ?? "",
      ctaDescription: data.clickToAction?.description ?? "",
      ctaButtonText: data.clickToAction?.buttonText ?? "",
      ctaIcon: data.clickToAction?.icon ?? "",
      ctaIconImageChoice: data.clickToAction?.iconType ?? IconType.None,
      ctaImage: data.clickToAction?.image
        ? MediaModel.deserializeAPIModel(data.clickToAction.image)
        : undefined,
      ctaIconColor: data.clickToAction?.color
        ? ColorModel.deserializeAPIModel(data.clickToAction?.color)
        : undefined,
      showCtaButton: true,
      donationItem: data.donationAndPaymentDetails?.donationItemDetails
        ? DonationItemModel.deserialize(
            data.donationAndPaymentDetails?.donationItemDetails
          )
        : null,
      isCustomDonationItem:
        data.donationAndPaymentDetails?.allowCustomDonations ?? false,
      donationAmountsType:
        data.donationAndPaymentDetails?.amountSelection ??
        DonationAmountSelection.Custom,
      // customDonationAmounts:
      //   data.donationAndPaymentDetails?.?? [],
      allowCustomDonations:
        data.donationAndPaymentDetails?.allowCustomDonations ?? false,
      allowMonthlyDonations:
        data.donationAndPaymentDetails?.allowMonthlyDonations ?? false,
      merchantAccount: data.donationAndPaymentDetails?.merchantAccount
        ? MerchantAccount.deserializeAPIModel2(
            data.donationAndPaymentDetails?.merchantAccount
          )
        : new MerchantAccount(),
      donationFormType: data.donationAndPaymentDetails?.showPopUp
        ? DonationFormType.Popup
        : DonationFormType.Embedded,
      popupModalType: data.donationAndPaymentDetails?.donationItem
        ? DonationPopupModalType.DonationItem
        : DonationPopupModalType.Simple,
      pageName: data.pageName ?? "",
      donationRestrictionsEnabled:
        data.donationAndPaymentDetails?.restrictions ?? false,
      minDonationAmount:
        formatCurrencyAmount(
          data.donationAndPaymentDetails?.minimumAmount.toString()
        ) ?? "",
      maxDonationAmount: formatCurrencyAmount(
        data.donationAndPaymentDetails?.maximumAmount.toString() ?? ""
      ),
      surchargeEnabled:
        data.donationAndPaymentDetails?.surchargeEnabled ?? false,
      surchargePercentage: data.donationAndPaymentDetails?.surchargePercentage
        ? data.donationAndPaymentDetails.surchargePercentage.toString()
        : "",
      surchargeAskCustomerFirst:
        data.donationAndPaymentDetails?.surchargeAskCustomerFirst ?? false,
      customDonationAmounts:
        data.donationAndPaymentDetails?.customAmounts ?? undefined,
      selectedLists:
        data.donationAndPaymentDetails?.lists.map((item) =>
          ListInterestTagModel.deserialize(item)
        ) ?? [],
      selectedInterests:
        data.donationAndPaymentDetails?.interests.map((item) =>
          ListInterestTagModel.deserialize(item)
        ) ?? [],
      selectedTags:
        data.donationAndPaymentDetails?.tags.map((item) =>
          ListInterestTagModel.deserialize(item)
        ) ?? [],
      redirectOnVisit: data.donationAndPaymentDetails
        ?.redirectType as keyof typeof FundRaiserRedirectType,
      redirectWebsiteUrl:
        data.donationAndPaymentDetails?.redirectValue?.url ?? "",
      redirectMobilePage:
        data.donationAndPaymentDetails?.redirectValue?.id ?? "",
      redirectOtherFundraiserType:
        data.donationAndPaymentDetails?.redirectType ||
        RedirectOtherFundraiserType.ClosestGoal,
      redirectSpecificFundraiserIds:
        data.donationAndPaymentDetails?.redirectType === "Specific"
          ? data.donationAndPaymentDetails?.redirectValue?.ids
          : [],
      promoterGoal: data.marketing?.promoterGoalAmount ?? 0,
      closeOnGoalAchieved: data.marketing?.closeOnGoalAchieved ?? false,
      increaseGoalOnAchieved: data.marketing?.isIncreaseGoal ?? false,
      increaseGoalPercentage:
        data.marketing?.increaseGoalPercentage.toString() ?? "0",
      allowPromoterInvites: data.marketing?.allowPromotersToRecruit ?? false,
      includeRecruitedActivity:
        data.marketing?.includeRecruitedPromoterActivityInPromoterGoals ??
        false,
      rewards:
        data.marketing?.rewardsByAmount?.map((r, i) =>
          RewardModel.deserializeAPIModel(r, i)
        ) ?? [],
      ranks:
        data.marketing?.rewardByRank?.map((rk, i) =>
          RankAchievementModel.deserializeAPIModel(rk, i)
        ) ?? [],
      promoterGridData:
        data.leadboard?.map((l) =>
          PromoterProgressModel.deserializeAPIModel(l, data.id)
        ) || [],
      bannerImage: data.marketing?.sponserBanners
        ? MediaModel.deserializeAPIModel(data.marketing?.sponserBanners[0])
        : new MediaModel(),
      selectedLayout:
        data.design?.layout ?? FundRaiserTemplateLayoutType.Layout1,
      sectionVisibility: data.design
        ? FundraiserSectionVisibilityModel.deserializeAPIModel(data.design)
        : new FundraiserSectionVisibilityModel(),
      progressLayout: data.design?.sideBySideProgress
        ? "side-by-side"
        : "stacked",
      customColors: data.design?.customColors
        ? FundraiserCustomColorConfig.deserializeAPIModel(
            data.design?.customColors
          )
        : new FundraiserCustomColorConfig(),
      selectedColorSwatch: data.design?.colorSwatch ?? ColorSwatches.Default,
      donationButtonType: data.design?.buttonDesign?.solidButton
        ? "solid"
        : "gradient",
      donationButtonColors: FundraiserDonationButtonColors.deserializeAPIModel(
        data.design?.buttonDesign
      ),
      legalName: data.marketing?.legalDetails?.name ?? "",
      streetAddress1: data.marketing?.legalDetails?.address1 ?? "",
      streetAddress2: data.marketing?.legalDetails?.address2 ?? "",
      city: data.marketing?.legalDetails?.city ?? "",
      state: data.marketing?.legalDetails?.state ?? "",
      stateId: data.marketing?.legalDetails?.stateId ?? "",
      zip: data.marketing?.legalDetails?.zipCode ?? "",
      countryId: data.marketing?.legalDetails?.countryId ?? "",
      termsType:
        data.marketing?.legalDetails?.termsAndConditionType ??
        LegalTermsAndConditionType.None,
      termsUrl: data.marketing?.legalDetails?.referenceURL ?? "",
      termsUrlError: false,
      termsText: data.marketing?.legalDetails?.customTerms ?? "",
      termsTextError: false,
      termsRequired:
        data.marketing?.legalDetails?.requireDonorAcceptence ?? false,
      promoterCount: data.design?.totalLeadBoards ?? 0,
      recentDonationsCount: data.design?.totalLastDonations ?? 0,
      showDonationComments: data.design?.showDonationComments ?? true,
      layoutZones: layoutZones,
      donationCustomerFacingCheckOutDetails: data.donationPurchase
        ? CustomerFacingCheckOutModel.deserializePurchaseLinkAPIModel(
            data.donationPurchase
          )
        : null,
      leaderboard: data.leadboard?.map((l) =>
        PromoterProgressModel.deserializeAPIModel(l, data.id)
      ),
      // ?.slice(0, data.totalLeadboards)
      // ?.map((l) => PromoterProgressModel.deserializeAPIModel(l, data.id)),
      donationsBoard: data.donations?.map((d) =>
        FundraisersDonationsBoardResponse.deserializePublicViewAPIModel(d)
      ),
      dailyDonations:
        data.dailyDonations?.map((d) =>
          FundraiserDailyDonationsModel.deserializeAPIModel(d)
        ) || [],
      promoterProgress: data.promoterProgress
        ? PromoterProgressModel.deserializeAPIModel(
            data.promoterProgress,
            data.id
          )
        : new PromoterProgressModel(),
      relatedFundraisers:
        data.relatedFundRaisers?.map((r) =>
          RelatedFundRaisersModel.deserializeAPIModel(r)
        ) || [],
    });
  }

  static deserializeGridAPIModel(
    data: IAPIFundraiserGridModel
  ): CreateFundraiserModel {
    return new CreateFundraiserModel({
      id: data.id ? data.id : "",
      shortUniqueId: data.shortUniqueId ?? "",
      pageName: data.pageName ? data.pageName : "",
      createdDate: data.createdDate ? new Date(data.createdDate) : undefined,
      name: data.name,
      status: data.status,
      goal: data.goalAmount,
      totalRaisedAmount: data.totalRaisedAmount,
      activePromoters: data.totalPromoters,
      totalDonations: data.totalDonations,
      lastDonatedOn: data.lastDonatedOn
        ? new Date(data.lastDonatedOn)
        : undefined,
      mediaItems:
        data.medias?.map((m) => MediaModel.deserializeAPIModel(m)) || [],
      startDate: data.startDate ? new Date(data.startDate) : undefined,
      endDate: data.endDate ? new Date(data.endDate) : undefined,
      titleIcon: data.icon ?? undefined,
      fundraiserImage: data.image
        ? MediaModel.deserializeAPIModel(data.image)
        : undefined,
      iconImageChoice: (data.iconType as keyof typeof IconType) ?? "None",
      titleColor: data.color
        ? ColorModel.deserializeAPIModel(data.color)
        : undefined,
    });
  }
  createPurchaseAPIRequest(
    promoterId: string,
    donationAmount: number,
    payMonthly: boolean,
    paymentToken: string,
    paymentIntentId: string,
    donorInfo: DonorInfo,
    comments: string,
    // When true the donor is covering the surcharge - the API adds the fundraiser's
    // configured percentage on top of the donation amount server-side.
    coverSurcharge: boolean = false
  ): IAPICheckOutPurchaseModel {
    const item = this.donationCustomerFacingCheckOutDetails?.items?.[0];
    const state = donorInfo.countryDetails?.states?.find(
      (s) => s.id === donorInfo.state
    );
    const request: IAPICheckOutPurchaseModel = {
      id: this.id,
      comments: comments,
      isFromFundRaiser: true,
      accountId: this.accountId,
      purchaseItems: item
        ? [
            {
              itemId: item.itemId,
              itemType: item.itemType,
              quantity: item.quantity,
              hasVariants: item.hasVariants,
              variantId:
                item.hasVariants && item.selectedVariantIndex > -1
                  ? item.variants?.options[item.selectedVariantIndex].id
                  : "",
              priceId: item.price?.id,
              customAmount: donationAmount,
              isFreeIncluded: false,
              inMemoryOf: item.inMemoryOf,
              dedicatedTo: item.dedicatedTo,
              tributeMessage: item.tributeMessage,
              beneficiaries: item.beneficiaries,
              payMonthly: payMonthly,
              allowAnnualIncrease: item.allowAnnualIncrease,
              coverFee: item.coverFee,
              coverFeeMessage: item.coverFeeMessage,
              increasePercentage: item.annualIncreasePercentage,
              /* The donor's own "Anonymous Donation" choice from the donor step. This used to send
                 item.anonymousDonation, which nothing on the fundraiser page ever sets, so every
                 fundraiser donation was saved as not anonymous and the donor's name showed in the
                 Donors/Donations tabs and the exports even when they had ticked the box. */
              anonymousDonation: !!donorInfo.isAnonymous,
            },
          ]
        : [],
      addOns: [],
      paymentDetails: {
        amount: donationAmount,
        paymentMethodId: paymentToken,
        paymentIntentId: paymentIntentId,
      } as IAPICheckOutPaymentModel,
      billingAddress: {
        country: donorInfo.countryDetails?.name || "",
        city: donorInfo.city,
        zipCode: donorInfo.zip,
        countryCode: donorInfo.countryDetails?.code.toString() || "",
        state: state ? state.name : "",
        address1: donorInfo.streetAddress,
        address2: "",
        address3: "",
        countryId: donorInfo.countryDetails?.id,
        stateId: state?.id || "",
      },
      contactFields: [
        {
          field: CheckOutPayFields.FirstLastName,
          value: donorInfo.firstName + "|" + donorInfo.lastName,
          date: null,
          countryCode: null,
          countryId: null,
        },
        {
          field: CheckOutPayFields.EmailAddress,
          value: donorInfo.email,
          date: null,
          countryCode: null,
          countryId: null,
        },
        {
          field: CheckOutPayFields.MobileNumber,
          value: donorInfo.phone,
          date: null,
          countryCode: donorInfo.phoneCountryCode,
          countryId: donorInfo.phoneCountryId,
        },
      ],
      shippingAddress: null,
      coverProcessingCost: coverSurcharge,
      promocodeId: "",
      promoterId: promoterId ? promoterId : undefined,
    };
    return request;
  }

  static deserializePromoters(
    fundraiserId: string,
    defaultPromoterGoal: number,
    promoterCount: number,
    contacts: ContactReferenceModel[]
  ): PromoterProgressModel[] {
    const newPromoters: PromoterProgressModel[] = contacts.map((contact) => ({
      id: contact.id,
      contactId: contact.id,
      mindmeContactId: contact.mindmeContactId,
      firstName: contact.firstName,
      lastName: contact.lastName,
      displayName: `${contact.firstName} ${contact.lastName}`,
      name: `${contact.firstName} ${contact.lastName}`,
      email: contact.displayEmail,
      mobile: contact.displayMobileNumber,
      countryId: contact.displayMobile?.countryId || "",
      countryCode: contact.displayMobile?.countryCode || "",
      totalRaisedAmount: 0,
      goalAmount: defaultPromoterGoal || 0,
      rank: promoterCount + 1,
      fundraiserId: fundraiserId,
      isActive: true,
      imageURL: "",
      totalDonations: 0,
      isNew: contact.isNew === true,
    }));
    return newPromoters;
  }

  static updatePromoter = (
    promoters: PromoterProgressModel[],
    promoterId: string,
    updatedPromoter: PromoterProgressModel
  ) => {
    const index = promoters.findIndex((p) => p.id === promoterId);
    if (index !== -1) {
      promoters[index] = updatedPromoter;
    }
    return promoters;
  };
}

// export class FundRaiserLinkedMedia implements IFundRaiserLinkedMedia {
//   mediaId: string;
//   fullURL?: string;
//   s3Key?: string;
//   src?: string;
//   type: MediaType;
//   title?: string;
//   description?: string;
//   order?: number;
//   constructor(data: Partial<FundRaiserLinkedMedia> = {}) {
//     this.mediaId = data.mediaId ?? "";
//     this.fullURL = data.fullURL ?? "";
//     this.s3Key = data.s3Key ?? "";
//     this.type = data.type ?? MediaType.Image;
//     this.title = data.title ?? "";
//     this.src = data.src ?? "";
//     this.description = data.description ?? "";
//     this.order = data.order ?? 0;
//   }

//   static deserializeAPIModel(
//     data: IAPILinkedMediaModel
//   ): FundRaiserLinkedMedia {
//     return new FundRaiserLinkedMedia({
//       mediaId: data.mediaId,
//       fullURL: data.fullURL,
//       s3Key: data.s3Key,
//       src: data.fullURL,
//       type: data.type as MediaType,
//     });
//   }
// }

export class OpenAISettingsModel {
  id: string = "";
  apiKey: string = "";
  textModel: string = "gpt-4o";
  imageModel: string = "gpt-image-1";

  constructor(data?: Partial<OpenAISettingsModel>) {
    this.id = data?.id ?? "";
    this.apiKey = data?.apiKey ?? "";
    this.textModel = data?.textModel ?? "gpt-4o";
    this.imageModel = data?.imageModel ?? "gpt-image-1";
  }

  static deserializeAPIModel(
    data: IUpdateOpenAISettingsRequestModel
  ): OpenAISettingsModel {
    return new OpenAISettingsModel({
      id: data.id,
      apiKey: data.apiKey,
      textModel: data.textModel,
      imageModel: data.imageModel,
    });
  }

  createSaveRequestModel(accountId: string): ISaveOpenAISettingsRequestModel {
    return {
      apiKey: this.apiKey,
      textModel: this.textModel,
      imageModel: this.imageModel,
      accountId: accountId,
    };
  }

  createUpdateRequestModel(
    accountId: string
  ): IUpdateOpenAISettingsRequestModel {
    return {
      id: this.id,
      apiKey: this.apiKey,
      textModel: this.textModel,
      imageModel: this.imageModel,
      accountId: accountId,
    };
  }
}

export class FundraiserSectionVisibilityModel implements ISectionVisibility {
  cta: boolean;
  donation: boolean;
  recent: boolean;
  impact: boolean;
  promoters: boolean;
  media: boolean;
  mission: boolean;
  progress: boolean;
  stats: boolean;
  fundraiserProgress: boolean;
  promoterProgress: boolean;

  header?: boolean;
  description?: boolean;
  sponsor?: boolean;

  constructor(data?: Partial<FundraiserSectionVisibilityModel>) {
    this.cta = data?.cta ?? true;
    this.donation = data?.donation ?? true;
    this.recent = data?.recent ?? true;
    this.impact = data?.impact ?? true;
    this.promoters = data?.promoters ?? true;
    this.media = data?.media ?? true;
    this.mission = data?.mission ?? true;
    this.progress = data?.progress ?? true;
    this.stats = data?.stats ?? true;
    this.fundraiserProgress = data?.fundraiserProgress ?? true;
    this.promoterProgress = data?.promoterProgress ?? true;
    this.header = data?.header ?? true;
    this.description = data?.description ?? true;
    this.sponsor = data?.sponsor ?? true;
  }

  static deserializeAPIModel(
    data: IFundRaiserPageDesign
  ): FundraiserSectionVisibilityModel {
    return new FundraiserSectionVisibilityModel({
      cta: data.showCallToAction,
      donation: data.showDonationForm,
      recent: data.showRecentDonations,
      impact: data.showImpactStats,
      promoters: data.showPromotersProgress,
      media: data.showHeroMedia,
      mission: data.showMissionSection,
      progress: data.showProgress,
      stats: data.showStats,
      fundraiserProgress: data.fundRaiserProgress,
      promoterProgress: data.promoterProgress,
      header: data.showHeader,
      description: true,
      sponsor: true,
    });
  }
}

export class FundRaiserGridModel {
  id: string;
  createdDate: Date;
  name: string;
  status: keyof typeof FundRaiserStatus;
  goalAmount: number;
  totalRaisedAmount: number;
  totalPromoters: number;
  totalDonations: number;
  lastDonatedOn: Date | null;
  medias?: MediaModel[];
  startDate?: Date;
  endDate?: Date; // Optional endDate for expiration badge logic

  constructor(data: Partial<FundRaiserGridModel> = {}) {
    this.id = data.id ?? "";
    this.createdDate = data.createdDate
      ? new Date(data.createdDate)
      : new Date();
    this.name = data.name ?? "";
    this.status = data.status ?? "Draft";
    this.goalAmount = data.goalAmount ?? 0;
    this.totalRaisedAmount = data.totalRaisedAmount ?? 0;
    this.totalPromoters = data.totalPromoters ?? 0;
    this.totalDonations = data.totalDonations ?? 0;
    this.lastDonatedOn = data.lastDonatedOn ?? null;
    this.medias = data.medias?.map((m) => new MediaModel(m)) || [];
    this.startDate = data.startDate;
    this.endDate = data.endDate;
  }

  getPrimaryImageToShow(): string {
    if (this.medias && this.medias.length > 0) {
      return (
        this.medias.filter((m) => m.type?.toLocaleLowerCase() === "image")[0]
          ?.fullURL || ""
      );
    }
    return "";
  }

  static deserializeAPIModel(
    data: IAPIFundraiserGridModel
  ): FundRaiserGridModel {
    return new FundRaiserGridModel({
      id: data.id ?? data.id ?? "",
      createdDate: data.createdDate ? new Date(data.createdDate) : new Date(),
      name: data.name,
      status: data.status,
      goalAmount: data.goalAmount,
      totalRaisedAmount: data.totalRaisedAmount,
      totalPromoters: data.totalPromoters,
      totalDonations: data.totalDonations,
      lastDonatedOn: data.lastDonatedOn ? new Date(data.lastDonatedOn) : null,
      medias: data.medias?.map((m) => MediaModel.deserializeAPIModel(m)) || [],
      startDate: data.startDate ? new Date(data.startDate) : undefined,
      endDate: data.endDate ? new Date(data.endDate) : undefined,
    });
  }
}

export class FundraiserCustomColorConfig {
  background: ColorModel;
  primary: ColorModel;
  secondary: ColorModel;
  accent: ColorModel;
  text: ColorModel;

  constructor(data?: Partial<FundraiserCustomColorConfig>) {
    this.background = data?.background
      ? new ColorModel(data.background)
      : new ColorModel({
          hex: "#ffffff",
          rgba: "rgba(255, 255, 255, 1)",
          rgbValue: new RGBA({ r: "255", g: "255", b: "255", a: "1" }),
        });
    this.primary = data?.primary
      ? new ColorModel(data.primary)
      : new ColorModel({
          hex: "#10B981",
          rgba: "rgba(16, 185, 129, 1)",
          rgbValue: new RGBA({ r: "16", g: "185", b: "129", a: "1" }),
        });
    this.secondary = data?.secondary
      ? new ColorModel(data.secondary)
      : new ColorModel({
          hex: "#3B82F6",
          rgba: "rgba(59, 130, 246, 1)",
          rgbValue: new RGBA({ r: "59", g: "130", b: "246", a: "1" }),
        });
    this.accent = data?.accent
      ? new ColorModel(data.accent)
      : new ColorModel({
          hex: "#8B5CF6",
          rgba: "rgba(139, 92, 246, 1)",
          rgbValue: new RGBA({ r: "139", g: "92", b: "246", a: "1" }),
        });
    this.text = data?.text
      ? new ColorModel(data.text)
      : new ColorModel({
          hex: "#1E293B",
          rgba: "rgba(30, 41, 59, 1)",
          rgbValue: new RGBA({ r: "30", g: "41", b: "59", a: "1" }),
        });
  }

  static deserializeAPIModel(
    data: IFundRaiserCustomColors
  ): FundraiserCustomColorConfig {
    return new FundraiserCustomColorConfig({
      background: data.background
        ? ColorModel.deserializeAPIModel(data.background)
        : undefined,
      primary: data.primary
        ? ColorModel.deserializeAPIModel(data.primary)
        : undefined,
      secondary: data.secondary
        ? ColorModel.deserializeAPIModel(data.secondary)
        : undefined,
      accent: data.accent
        ? ColorModel.deserializeAPIModel(data.accent)
        : undefined,
      text: data.text ? ColorModel.deserializeAPIModel(data.text) : undefined,
    });
  }
}

export class FundraiserDonationButtonColors {
  text: ColorModel;
  primary: ColorModel;
  secondary: ColorModel;
  constructor(data?: Partial<FundraiserDonationButtonColors>) {
    this.text = data?.text
      ? new ColorModel(data.text)
      : new ColorModel({
          hex: "#ffffff",
          rgba: "rgba(255, 255, 255, 1)",
          rgbValue: new RGBA({ r: "255", g: "255", b: "255", a: "1" }),
        });
    this.primary = data?.primary
      ? new ColorModel(data.primary)
      : new ColorModel({
          hex: "#12B981",
          rgba: "rgba(18, 185, 129, 1)",
          rgbValue: new RGBA({ r: "18", g: "185", b: "129", a: "1" }),
        });
    this.secondary = data?.secondary
      ? new ColorModel(data.secondary)
      : new ColorModel({
          hex: "#3986EF",
          rgba: "rgba(57, 134, 239, 1)",
          rgbValue: new RGBA({ r: "57", g: "134", b: "239", a: "1" }),
        });
  }

  static deserializeAPIModel(
    data: IDonationButtonDesign | undefined
  ): FundraiserDonationButtonColors {
    return new FundraiserDonationButtonColors({
      text: data?.text ? ColorModel.deserializeAPIModel(data.text) : undefined,
      primary: data?.solidButton
        ? data?.color
          ? ColorModel.deserializeAPIModel(data.color)
          : undefined
        : data?.startColor
          ? ColorModel.deserializeAPIModel(data.startColor)
          : undefined,
      secondary: data?.solidButton
        ? undefined
        : data?.endColor
          ? ColorModel.deserializeAPIModel(data.endColor)
          : undefined,
    });
  }
}

export class FundRaiserStatsModel {
  id: string;
  label: string;
  value: string;
  order: number;
  constructor(data?: Partial<FundRaiserStatsModel>) {
    this.id = data?.id ?? "";
    this.label = data?.label ?? "";
    this.value = data?.value ?? "";
    this.order = data?.order ?? 0;
  }

  static deserializeAPIModel(
    data: IFundStatsIAPIResponse,
    order: number
  ): FundRaiserStatsModel {
    return new FundRaiserStatsModel({
      id: "",
      label: data.name,
      value: data.value,
      order: order,
    });
  }
}
export class FundraisersDonationsBoardResponse {
  displayName: string;
  firstName: string;
  lastName: string;
  imageURL: string;
  amount: number;
  donatedOn: Date;
  comment?: string;
  isAnonymous?: boolean;
  donationId: string;
  isMonthlyDonation: boolean;
  id: string;
  donorName: string;
  donorEmail?: string;
  donorMobile?: ContactPhoneModel;
  timestamp: Date;
  fundraiserId: string;
  promoterId?: string;
  isMonthly?: boolean;
  paymentMethod: PaymentMethodModel;
  merchantAccount: MerchantAccount;
  paymentTransactionId: string;
  createdDate?: Date;
  amountRefunded?: number;

  constructor(data?: Partial<FundraisersDonationsBoardResponse>) {
    this.displayName = data?.displayName ?? "";
    this.firstName = data?.firstName ?? "";
    this.lastName = data?.lastName ?? "";
    this.imageURL = data?.imageURL ?? "";
    this.amount = data?.amount ?? 0;
    this.donatedOn = data?.donatedOn ?? new Date();
    this.comment = data?.comment ?? "";
    this.isAnonymous = data?.isAnonymous ?? false;
    this.donationId = data?.donationId ?? "";
    this.isMonthlyDonation = data?.isMonthlyDonation ?? false;
    this.id = data?.id ?? "";
    this.amount = data?.amount ?? 0;
    this.donorName = data?.donorName ?? "";
    this.donorEmail = data?.donorEmail ?? "";
    this.donorMobile = data?.donorMobile
      ? new ContactPhoneModel(data.donorMobile)
      : undefined;
    this.timestamp = data?.timestamp ? new Date(data.timestamp) : new Date();
    this.fundraiserId = data?.fundraiserId ?? "";
    this.promoterId = data?.promoterId ?? "";
    this.isMonthly = data?.isMonthly ?? false;
    this.paymentMethod = data?.paymentMethod
      ? new PaymentMethodModel(data.paymentMethod)
      : new PaymentMethodModel();
    this.merchantAccount = data?.merchantAccount
      ? new MerchantAccount(data.merchantAccount)
      : new MerchantAccount();
    this.paymentTransactionId = data?.paymentTransactionId || "";
    this.createdDate = data?.createdDate
      ? new Date(data.createdDate)
      : undefined;
    this.amountRefunded = data?.amountRefunded ?? 0;
  }

  getDisplayName = () => {
    if (this.isAnonymous) {
      return "Anonymous Donor";
    }
    // In admin views, always show actual name regardless of isAnonymous flag
    if (this.firstName && this.lastName) {
      return `${this.firstName} ${this.lastName}`;
    }
    // Fallback to donorName, but exclude generic anonymous names
    return this.displayName || "Unknown Donor";
  };

  static deserializePublicViewAPIModel(
    data: IFundraisersDonationsBoardResponse
  ): FundraisersDonationsBoardResponse {
    return new FundraisersDonationsBoardResponse({
      id: data.paymentTransactionId,
      displayName: data.displayName,
      firstName: data.firstName,
      lastName: data.lastName,
      imageURL: data.imageURL,
      amount: data.amount,
      // Also account time zone carrying a "Z"; see the note in deserializeAPIModel below.
      donatedOn: data.donatedOn
        ? UtilityMethods.removeZFromDateString(data.donatedOn)
        : undefined,
      comment: data.comments ?? "",
      isAnonymous: data.isAnonymous ?? false,
      donationId: data.donationId,
    });
  }

  static deserializeAPIModel(
    data: IFundraisersDonationsBoardResponse
  ): FundraisersDonationsBoardResponse {
    return new FundraisersDonationsBoardResponse({
      id: data.paymentTransactionId,
      displayName: data.contact?.firstName + " " + data.contact?.lastName,
      firstName: data.contact?.firstName,
      lastName: data.contact?.lastName,
      imageURL: data.imageURL,
      amount: data.amount,
      /* The API has already shifted these into the account's time zone but still sends them with a
         trailing "Z", so `new Date(...)` read them as UTC and shifted them a second time into
         whatever zone the viewer's machine is in. That is why this tab disagreed with the export
         and with the Money grids, which have always stripped the "Z" here. */
      donatedOn: data.createdDate
        ? UtilityMethods.removeZFromDateString(data.createdDate)
        : undefined,
      timestamp: data.createdDate
        ? UtilityMethods.removeZFromDateString(data.createdDate)
        : undefined,
      comment: data.comments ?? "",
      isAnonymous: data.anonymousDonation ?? false,
      donationId: data.donationId,
      createdDate: data.createdDate
        ? UtilityMethods.removeZFromDateString(data.createdDate)
        : undefined,
      fundraiserId: data.fundRaiserId,
      paymentTransactionId: data.paymentTransactionId,
      donorEmail:
        data.contact?.email ||
        data.contact?.homeEmail ||
        data.contact?.workEmail ||
        data.contact?.otherEmail,
      donorMobile:
        (data.contact?.homePhone &&
          ContactPhoneModel.deserializeAPIModel(data.contact.homePhone)) ||
        (data.contact?.mobilePhone &&
          ContactPhoneModel.deserializeAPIModel(data.contact.mobilePhone)) ||
        (data.contact?.workPhone &&
          ContactPhoneModel.deserializeAPIModel(data.contact.workPhone)),
      paymentMethod: data.paymentMethod
        ? PaymentMethodModel.deserializeAPIModel(data.paymentMethod)
        : new PaymentMethodModel(),
      isMonthlyDonation: data.isMonthlyDonation ?? false,
      merchantAccount: data.merchantAccount
        ? MerchantAccount.deserializeAPIModel2(data.merchantAccount)
        : new MerchantAccount(),
      amountRefunded: data.amountRefunded ?? 0,
    });
  }
}

// export class FundraisersPromoterProgressResponse {
//   displayName: string;
//   firstName: string;
//   lastName: string;
//   imageURL: string;
//   rank: number;
//   goalAmount: number;
//   totalRaisedAmount: number;
//   id: string;

//   constructor(data?: Partial<FundraisersPromoterProgressResponse>) {
//     this.displayName = data?.displayName ?? "";
//     this.firstName = data?.firstName ?? "";
//     this.lastName = data?.lastName ?? "";
//     this.imageURL = data?.imageURL ?? "";
//     this.rank = data?.rank ?? 0;
//     this.goalAmount = data?.goalAmount ?? 0;
//     this.totalRaisedAmount = data?.totalRaisedAmount ?? 0;
//     this.id = data?.id ?? "";
//   }

//   static deserializeAPIModel(
//     data: IAPIPromoterProgressModel
//   ): FundraisersPromoterProgressResponse {
//     return new FundraisersPromoterProgressResponse({
//       displayName: data.displayName,
//       firstName: data.firstName,
//       lastName: data.lastName,
//       imageURL: data.imageURL,
//       rank: data.rank,
//       goalAmount: data.goalAmount,
//       totalRaisedAmount: data.totalRaisedAmount,
//       id: data.id,
//     });
//   }
// }

export class CategoryModel {
  id: string;
  name: string;

  constructor(data?: Partial<IAPIItemsGridModel>) {
    this.id = data?.id ?? "";
    this.name = data?.name ?? "";
  }

  static deserializeAPIModel(data: ICategoryModel): CategoryModel {
    return new CategoryModel({
      id: data.id || "",
      name: data.name,
    });
  }

  createAPIModel(): ICategoryModel {
    return {
      id: null,
      name: this.name,
    };
  }
}

export class FundraiserPublicViewPromoterResponseModel {
  displayName: string;
  firstName: string;
  lastName: string;
  imageURL: string;
  isCurrentPosition: boolean;

  constructor(data: Partial<FundraiserPublicViewPromoterResponseModel>) {
    this.displayName = data.displayName ?? "";
    this.firstName = data.firstName ?? "";
    this.lastName = data.lastName ?? "";
    this.imageURL = data.imageURL ?? "";
    this.isCurrentPosition = data.isCurrentPosition ?? false;
  }

  static deserializeAPIModel(
    data: IFundraiserPublicViewPromoterResponseModel
  ): FundraiserPublicViewPromoterResponseModel {
    return new FundraiserPublicViewPromoterResponseModel(data);
  }
}

export class FundraiserPublicViewRewardsByRank {
  rank: number;
  reward: string;
  promoters: FundraiserPublicViewPromoterResponseModel[];

  constructor(data: Partial<FundraiserPublicViewRewardsByRank>) {
    this.rank = data.rank ?? 0;
    this.reward = data.reward ?? "";
    this.promoters = (data.promoters ?? []).map((p) =>
      FundraiserPublicViewPromoterResponseModel.deserializeAPIModel(p)
    );
  }

  static deserializeAPIModel(
    data: IFundraiserPublicViewPromoterRewardResponseModel
  ): FundraiserPublicViewRewardsByRank {
    return new FundraiserPublicViewRewardsByRank({
      rank: data.rank,
      reward: data.reward,
      promoters: data.promoters.map(
        FundraiserPublicViewPromoterResponseModel.deserializeAPIModel
      ),
    });
  }
}

export class FundraiserDailyDonationsModel {
  date: Date;
  totalAmount: number;
  constructor(data: Partial<FundraiserDailyDonationsModel>) {
    this.date = data.date ? new Date(data.date) : new Date();
    this.totalAmount = data.totalAmount ?? 0;
  }
  static deserializeAPIModel(
    data: IFundraisersDailyDonations
  ): FundraiserDailyDonationsModel {
    return new FundraiserDailyDonationsModel({
      date: data.date ? new Date(data.date) : new Date(),
      totalAmount: data.totalAmount ?? 0,
    });
  }
}

export class RelatedFundRaisersModel {
  id: string;
  name: string;
  status: keyof typeof FundRaiserStatus;
  totalRaisedAmount: number;
  goalAmount: number;
  iconType: keyof typeof IconType;
  icon: string;
  iconColor: ColorModel;
  image?: MediaModel;
  constructor(data?: Partial<RelatedFundRaisersModel>) {
    this.id = data?.id ?? "";
    this.name = data?.name ?? "";
    this.status = data?.status ?? FundRaiserStatus.Draft;
    this.totalRaisedAmount = data?.totalRaisedAmount ?? 0;
    this.goalAmount = data?.goalAmount ?? 0;
    this.iconType = data?.iconType ?? IconType.None;
    this.icon = data?.icon ?? "";
    this.iconColor = data?.iconColor ?? new ColorModel();
    this.image = data?.image ? new MediaModel(data.image) : undefined;
  }
  static deserializeAPIModel(
    data: IAPIRelatedFundraisers
  ): RelatedFundRaisersModel {
    return new RelatedFundRaisersModel({
      id: data.id,
      name: data.name,
      status: data.status as keyof typeof FundRaiserStatus,
      totalRaisedAmount: data.totalRaisedAmount,
      goalAmount: data.goalAmount,
      iconType: data.iconType as keyof typeof IconType,
      icon: data.icon,
      iconColor: data.iconColor
        ? ColorModel.deserializeAPIModel(data.iconColor)
        : new ColorModel(),
    });
  }
}
